// PreceptorEvaluationForm.jsx
import React, { useState, useEffect } from 'react';
import { getActionErrorMessage } from '../utils/errorHandler';
import '../styles/PreceptorEvaluationForm.css';
import "../styles/theme.css";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PreceptorEvaluationForm = () => {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [evaluation, setEvaluation] = useState({
    degree: 0,
    description: '',
    comment: ''
  });
  const [students, setStudents] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [evaluationPeriods, setEvaluationPeriods] = useState([]);
  const [currentRotation, setCurrentRotation] = useState(null);
  const [isEvaluationPeriod, setIsEvaluationPeriod] = useState(false);

  useEffect(() => {
    const fetchEvaluationPeriods = async () => {
      try {
        const currentYear = new Date().getMonth() >= 7 ? 
          new Date().getFullYear() : 
          new Date().getFullYear() - 1;

        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Fetching for year:', currentYear);

        const response = await fetch(getApiUrl(`config/get_preceptor_evaluation_dates/${currentYear}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const data = await response.json();
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Raw response data:', data);

        const periods = data.preceptor_to_student_eval || [];
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Processed periods:', periods);
        setEvaluationPeriods(periods);

        const now = new Date();

        if (periods.length === 0) {
          return;
        }

        for (let i = 0; i < periods.length; i++) {
          if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`Checking period ${i}:`, periods[i]);
          const start = new Date(periods[i].startDate);
          const end = new Date(periods[i].endDate);

          if (now >= start && now <= end) {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Found matching period:', i);
            setIsEvaluationPeriod(true);
            setCurrentRotation(i);
            break;
          }
        }
      } catch (err) {
        console.error('Error in fetchEvaluationPeriods:', err);
        setError('Failed to fetch evaluation periods');
      }
    };

    fetchEvaluationPeriods();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!isEvaluationPeriod || currentRotation === null) return;

      try {
        const currentDate = new Date();
        const curriculumYear = currentDate.getMonth() >= 7 ? 
          currentDate.getFullYear() : 
          currentDate.getFullYear() - 1;

        const response = await axiosInstance.get(
          getApiUrl(`crud/get_students_by_preceptor_rotation/${currentRotation}?year=${curriculumYear}`),
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch students');
        }

        const data = await response.json();
        
        // Check if data is an array
        if (Array.isArray(data)) {
          if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Students array:', data);
          setStudents(data);
        } else {
          console.error('Expected array of students, got:', data);
          setStudents([]);
          setError('Invalid student data received');
        }
      } catch (err) {
        console.error('Failed to fetch students:', err);
        setStudents([]);
        setError('Failed to fetch students');
      }
    };

    fetchStudents();
  }, [isEvaluationPeriod, currentRotation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
              const response = await fetch(getApiUrl('crud/send_preceptor_to_student_evaluation/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: selectedStudent,
          eval_degree: evaluation.degree,
          eval_description: evaluation.description,
          eval_comment: evaluation.comment
        })
      });

      if (!response.ok) throw new Error('Failed to submit evaluation');
      
      setSuccess('Evaluation submitted successfully!');
      setSelectedStudent('');
      setEvaluation({
        degree: 0,
        description: '',
        comment: ''
      });
    } catch (err) {
      setError(getActionErrorMessage(err, 'load evaluation form'));
    } finally {
      setLoading(false);
    }
  };

  const renderEvaluationSchedule = () => {
    return (
      <div className="evaluation-schedule">
        <h2>Evaluation Periods Schedule</h2>
        <div className="schedule-list">
          {evaluationPeriods.map((period, index) => (
            <div key={index} className="schedule-item">
              <h3>Rotation {index + 1}</h3>
              <p>Start: {formatDate(period.startDate)}</p>
              <p>End: {formatDate(period.endDate)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="content-box">
        <div className="modern-schedule-header">
          <div>
            <h1 className="page-title">Student Evaluation</h1>
            <p className="schedule-subtitle">Evaluate student performance during rotations</p>
          </div>
        </div>
        <div className="modern-form-container">
          <div className="evaluation-card">
            <h1 className="evaluation-title">Student Evaluation</h1>
            
            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}
            
            {!isEvaluationPeriod ? (
              <div className="closed-form-container">
                <div className="alert info">
                  Evaluation form is currently closed. Evaluations can only be submitted during designated evaluation periods.
                </div>
                {renderEvaluationSchedule()}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="evaluation-form">
                <div className="form-group">
                  <label>Select Student</label>
                  <select
                    value={selectedStudent}
                    onChange={(e) => setSelectedStudent(e.target.value)}
                    required
                  >
                    <option value="">Choose a student</option>
                    {Array.isArray(students) && students.map((student) => ( // Add Array.isArray check
                      <option key={student.id} value={student.id}>
                        {student.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Evaluation Score (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={evaluation.degree}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      degree: parseFloat(e.target.value)
                    })}
                    required
                    placeholder="Enter score"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={evaluation.description}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      description: e.target.value
                    })}
                    required
                    placeholder="Brief description of the evaluation"
                  />
                </div>

                <div className="form-group">
                  <label>Comments</label>
                  <textarea
                    value={evaluation.comment}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      comment: e.target.value
                    })}
                    rows="4"
                    required
                    placeholder="Detailed comments about the student's performance"
                  />
                </div>

                <button
                  type="submit"
                  className={`submit-button ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Evaluation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreceptorEvaluationForm;